import React, { useEffect, useMemo, useState } from 'react';
import './styles.scss';
import { categoryIcon } from './icons';
import { itemIconUrl } from './itemIcons';

interface RecipeIngredient { itemFormId: string; name: string; count: number; owned: number }
interface RecipeEntry {
  recipeId: string; name: string; category: string; tier: number; requiredRankId: number;
  tierLabel?: string; isArtifact?: boolean; culturalStyle?: string;
  discoverable?: boolean; discovered?: boolean;
  unlocked: boolean; craftable: boolean;
  output: { itemFormId: string; count: number };
  ingredients: RecipeIngredient[];
}
interface RankInfo { id: number; name: string }
interface MenuDetail {
  activity: string;
  professionId: string;
  currentRankId: number;
  recipes: RecipeEntry[];
  ranks: RankInfo[];
  canSign?: boolean;
  signatureMaxLength?: number;
}

type Filter = 'all' | 'craftable';

const CRAFT_MENU_CHOOSE_PREFIX = 'craft-menu-choose:';
const CRAFT_MENU_CLOSE_MESSAGE = 'craft-menu-close';

// Same click-grace guard as ProfessionsMenu/TargetMenu - browser focus flaps
// while the overlay is toggled on, so ignore any click for a brief window
// after opening.
const CLICK_GRACE_MS = 250;

const ACTIVITY_TITLE: Record<string, string> = {
  smith: 'Forge',
  smelt: 'Fonderie',
  weave: 'Métier à tisser',
  cook: 'Marmite',
  brew: "Laboratoire d'alchimie",
  tan: 'Chevalet à tanner',
};

const RecipeIcon: React.FC<{ recipe: RecipeEntry; className: string }> = ({ recipe, className }) => {
  const url = itemIconUrl(recipe.output.itemFormId);
  return (
    <span className={className}>
      {url ? <img src={url} alt="" /> : categoryIcon(recipe.category)}
    </span>
  );
};

const missingCount = (r: RecipeEntry) => r.ingredients.filter((i) => i.owned < i.count).length;

const CraftMenu: React.FC = () => {
  const [visible, setVisible] = useState(false);
  const [detail, setDetail] = useState<MenuDetail | null>(null);
  // Selecting a recipe only previews it. Only the "Fabriquer" button of the
  // detail pane sends the craft command, so a stray click never consumes
  // materials or starts the minigame.
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [filter, setFilter] = useState<Filter>('all');
  const [query, setQuery] = useState('');
  const [signature, setSignature] = useState('');
  const openedAtRef = React.useRef(0);

  useEffect(() => {
    const showHandler = (e: Event) => {
      const d = (e as CustomEvent).detail as MenuDetail;
      setDetail(d);
      // Start on something useful: the first recipe the player can make now.
      const first = d.recipes.find((r) => r.craftable) ?? d.recipes.find((r) => r.unlocked) ?? d.recipes[0];
      setSelectedId(first ? first.recipeId : null);
      setFilter('all');
      setQuery('');
      setSignature('');
      setVisible(true);
      openedAtRef.current = Date.now();
    };
    const hideHandler = () => setVisible(false);

    window.addEventListener('skymp5-client:showCraftMenu', showHandler);
    window.addEventListener('skymp5-client:hideCraftMenu', hideHandler);
    return () => {
      window.removeEventListener('skymp5-client:showCraftMenu', showHandler);
      window.removeEventListener('skymp5-client:hideCraftMenu', hideHandler);
    };
  }, []);

  const rankName = useMemo(() => {
    const byId = new Map((detail?.ranks ?? []).map((r) => [r.id, r.name]));
    return (id: number) => byId.get(id) ?? `Rang ${id}`;
  }, [detail]);

  const craftableCount = useMemo(
    () => (detail ? detail.recipes.filter((r) => r.craftable).length : 0),
    [detail],
  );

  // Recipes grouped by required rank, ascending, after the search and filter.
  const groups = useMemo(() => {
    if (!detail) return [];
    const q = query.trim().toLowerCase();
    const byRank = new Map<number, RecipeEntry[]>();
    for (const r of detail.recipes) {
      if (filter === 'craftable' && !r.craftable) continue;
      if (q && !r.name.toLowerCase().includes(q)) continue;
      const list = byRank.get(r.requiredRankId) ?? [];
      list.push(r);
      byRank.set(r.requiredRankId, list);
    }
    return [...byRank.entries()].sort((a, b) => a[0] - b[0]);
  }, [detail, filter, query]);

  const selected = useMemo(
    () => detail?.recipes.find((r) => r.recipeId === selectedId) ?? null,
    [detail, selectedId],
  );

  const send = (message: string) => {
    const w = window as unknown as { skyrimPlatform?: { sendMessage?: (m: string) => void } };
    w.skyrimPlatform?.sendMessage?.(message);
  };

  const close = () => {
    setVisible(false);
    send(CRAFT_MENU_CLOSE_MESSAGE);
  };

  const select = (e: React.MouseEvent, recipe: RecipeEntry) => {
    if (e.detail === 0) return;
    if (Date.now() - openedAtRef.current < CLICK_GRACE_MS) return;
    setSelectedId(recipe.recipeId);
  };

  // The only place that sends the craft command.
  const signatureMax = detail?.signatureMaxLength ?? 64;
  const confirmCraft = (e: React.MouseEvent, recipe: RecipeEntry) => {
    if (e.detail === 0 || !recipe.craftable || !recipe.unlocked) return;
    setVisible(false);
    // Optional dedication: "craft-menu-choose:<recipeId>|<text>". The server
    // sanitizes it again and ignores it if the rank does not allow signing.
    const dedication = detail?.canSign
      ? signature.replace(/\p{Cc}+/gu, ' ').trim().slice(0, signatureMax)
      : '';
    send(CRAFT_MENU_CHOOSE_PREFIX + recipe.recipeId + (dedication ? '|' + dedication : ''));
  };

  if (!visible || !detail) return null;

  const total = detail.recipes.length;

  return (
    <div className="craft-menu-overlay">
      <div className="craft-menu-shell" role="dialog" aria-label={ACTIVITY_TITLE[detail.activity] ?? 'Fabrication'}>
        <header className="craft-menu-header">
          <div className="craft-menu-header__text">
            <div className="craft-menu-header__brand">Altherys RP</div>
            <h1 className="craft-menu-header__title">{ACTIVITY_TITLE[detail.activity] ?? 'Fabrication'}</h1>
          </div>
          <div className="craft-menu-header__stats">
            <span className="craft-menu-chip craft-menu-chip--accent">{rankName(detail.currentRankId)}</span>
            <span className="craft-menu-header__count">
              <strong>{craftableCount}</strong> fabricable{craftableCount > 1 ? 's' : ''} sur {total}
            </span>
          </div>
        </header>

        <div className="craft-menu-main">
          <section className="craft-menu-list" aria-label="Recettes">
            <div className="craft-menu-toolbar">
              <input
                type="text"
                className="craft-menu-search"
                placeholder="Rechercher une recette"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                aria-label="Rechercher une recette"
              />
              <div className="craft-menu-filter" role="tablist">
                <button
                  type="button"
                  role="tab"
                  aria-selected={filter === 'all'}
                  className={'craft-menu-filter__btn' + (filter === 'all' ? ' craft-menu-filter__btn--active' : '')}
                  onClick={() => setFilter('all')}
                >
                  Tout
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={filter === 'craftable'}
                  className={'craft-menu-filter__btn' + (filter === 'craftable' ? ' craft-menu-filter__btn--active' : '')}
                  onClick={() => setFilter('craftable')}
                >
                  Fabricable
                </button>
              </div>
            </div>

            <div className="craft-menu-scroll">
              {groups.length === 0 && (
                <div className="craft-menu-empty">
                  {filter === 'craftable'
                    ? 'Rien n\'est fabricable avec ce que tu portes.'
                    : 'Aucune recette ne correspond.'}
                </div>
              )}

              {groups.map(([rankId, recipes]) => (
                <div className="craft-menu-group" key={rankId}>
                  <div className="craft-menu-group__heading">
                    <span>{rankName(rankId)}</span>
                    <span className="craft-menu-group__count">{recipes.length}</span>
                  </div>
                  {recipes.map((r) => {
                    const missing = missingCount(r);
                    return (
                      <button
                        type="button"
                        key={r.recipeId}
                        className={[
                          'craft-menu-recipe',
                          r.recipeId === selectedId ? 'craft-menu-recipe--selected' : '',
                          !r.unlocked ? 'craft-menu-recipe--locked' : '',
                        ].filter(Boolean).join(' ')}
                        onClick={(e) => select(e, r)}
                      >
                        <RecipeIcon recipe={r} className="craft-menu-recipe__icon" />
                        <span className="craft-menu-recipe__name">{r.name}</span>
                        {!r.unlocked ? (
                          <span className="craft-menu-status craft-menu-status--locked">Verrouillé</span>
                        ) : r.craftable ? (
                          <span className="craft-menu-status craft-menu-status--ok">Fabricable</span>
                        ) : (
                          <span className="craft-menu-status craft-menu-status--missing">
                            {missing} manquant{missing > 1 ? 's' : ''}
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              ))}
            </div>
          </section>

          <section className="craft-menu-detail" aria-label="Détail de la recette">
            {selected ? (
              <>
                <div className="craft-menu-detail__hero">
                  <RecipeIcon recipe={selected} className="craft-menu-detail__icon" />
                  <div className="craft-menu-detail__title">
                    <h2 className="craft-menu-detail__name">{selected.name}</h2>
                    <div className="craft-menu-detail__tags">
                      <span className="craft-menu-chip">
                        {selected.tierLabel ?? rankName(selected.requiredRankId)}
                      </span>
                      {selected.output.count > 1 && (
                        <span className="craft-menu-chip">Produit {selected.output.count}</span>
                      )}
                      {selected.isArtifact && <span className="craft-menu-chip craft-menu-chip--accent">Artefact</span>}
                    </div>
                  </div>
                </div>

                <h3 className="craft-menu-detail__heading">Ingrédients</h3>
                <ul className="craft-menu-ingredients">
                  {selected.ingredients.map((ing, i) => {
                    const ok = ing.owned >= ing.count;
                    const ratio = ing.count > 0 ? Math.min(1, ing.owned / ing.count) : 1;
                    return (
                      <li
                        key={ing.itemFormId + i}
                        className={'craft-menu-ingredient' + (ok ? '' : ' craft-menu-ingredient--missing')}
                      >
                        <span className="craft-menu-ingredient__name">{ing.name}</span>
                        <span className="craft-menu-ingredient__qty">
                          {ing.owned}<span className="craft-menu-ingredient__need"> / {ing.count}</span>
                        </span>
                        <span className="craft-menu-ingredient__bar" aria-hidden="true">
                          <span style={{ width: `${Math.round(ratio * 100)}%` }} />
                        </span>
                      </li>
                    );
                  })}
                </ul>

                <div className="craft-menu-detail__footer">
                  {detail.canSign && selected.unlocked && (
                    <label className="craft-menu-signature">
                      <span className="craft-menu-signature__label">
                        Dédicace ({signatureMax} caractères max)
                      </span>
                      <input
                        type="text"
                        className="craft-menu-signature__input"
                        placeholder="Optionnel, signée de ton nom"
                        maxLength={signatureMax}
                        value={signature}
                        onChange={(e) => setSignature(e.target.value)}
                      />
                    </label>
                  )}
                  {!selected.unlocked ? (
                    <p className="craft-menu-detail__note">
                      Rang requis : {rankName(selected.requiredRankId)}.
                    </p>
                  ) : !selected.craftable ? (
                    <p className="craft-menu-detail__note craft-menu-detail__note--warn">
                      Il te manque des ingrédients pour cette fabrication.
                    </p>
                  ) : (
                    <p className="craft-menu-detail__note">Les ingrédients seront consommés au début.</p>
                  )}
                  <button
                    type="button"
                    className="craft-menu-confirm"
                    disabled={!selected.craftable || !selected.unlocked}
                    onClick={(e) => confirmCraft(e, selected)}
                  >
                    Fabriquer
                  </button>
                </div>
              </>
            ) : (
              <div className="craft-menu-empty craft-menu-empty--detail">Choisis une recette dans la liste.</div>
            )}
          </section>
        </div>

        <footer className="craft-menu-footer">
          <div className="craft-menu-footer__hint"><kbd>Échap</kbd> Fermer</div>
          <button type="button" className="craft-menu-close-btn" onClick={close}>Fermer</button>
        </footer>
      </div>
    </div>
  );
};

export default CraftMenu;
